import { defineNuxtPlugin } from '#app'
import posthog from 'posthog-js'
import type { PostHog } from 'posthog-js'
import { setAnalyticsClient } from '~/services/analytics'
import {
  createPostHogHelpCenterProvider,
  setHelpCenterProvider,
} from '~/services/helpCenter'
import { isIgnoredThirdPartyException, shouldDropPostHogEvent } from '~/utils/posthogErrorFilter'
import { shouldInitializePostHog } from '~/utils/posthogInit'

const noopPosthog = {
  capture: () => undefined,
  identify: () => undefined,
  captureException: () => undefined,
  group: () => undefined,
  reset: () => undefined,
} as unknown as PostHog

/**
 * Bundle Help Center / replay / exception extensions into the app so the SDK
 * does not fetch `/static/posthog-recorder.js` (and similar) at runtime.
 * Ad blockers match those filenames even on our first-party proxy.
 *
 * Still import these modules *before* `init`, but do not `await` them in the
 * Nuxt plugin — an async plugin blocks first paint on every authenticated route.
 */
const preloadPostHogExtensions = () => Promise.all([
  import('posthog-js/dist/conversations'),
  import('posthog-js/dist/surveys'),
  import('posthog-js/dist/posthog-recorder'),
  import('posthog-js/dist/exception-autocapture'),
])

export default defineNuxtPlugin((nuxtApp) => {
  const runtimeConfig = useRuntimeConfig()
  setHelpCenterProvider(null)

  // Skip PostHog initialization in development mode
  if (import.meta.env.MODE === 'development') {
    $log('PostHog analytics disabled in development mode')
    return {
      provide: {
        posthog: () => noopPosthog,
      },
    }
  }

  if (!shouldInitializePostHog(
    runtimeConfig.public.posthogPublicKey,
    runtimeConfig.public.quteeE2e === true,
  )) {
    $log('PostHog analytics disabled (missing token or QUTEE_E2E)')
    setAnalyticsClient(null)
    return {
      provide: {
        posthog: () => noopPosthog,
      },
    }
  }

  let posthogClient: PostHog | undefined

  const posthogReady = preloadPostHogExtensions().then(() => {
    // Omit asset_host so any remaining remote modules load from api_host
    // (same-origin proxy). Pointing asset_host at the PostHog CDN triggers CORS
    // for fetch-loaded extensions. Requires the backend /static/* proxy to return
    // decoded JS (see backend routers/ingest.py).
    posthogClient = posthog.init(runtimeConfig.public.posthogPublicKey, {
      api_host: runtimeConfig.public.posthogHost,
      ui_host: runtimeConfig.public.posthogUiHost,
      person_profiles: 'identified_only', // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // we add manual pageview capturing below
      capture_pageleave: true,
      // Prefer /static/<version>/<name>.js over legacy ?v= query URLs when a
      // remote module is still needed (avoids some filename-based block lists).
      strict_script_versioning: true,
      before_send: event => (event && shouldDropPostHogEvent(event) ? null : event),
      loaded: (loadedClient) => {
        if (import.meta.env.MODE === 'development') loadedClient.debug()
      },
    }) ?? undefined

    if (posthogClient) {
      setAnalyticsClient(posthogClient)
      if (runtimeConfig.public.helpCenterEnabled === true) {
        setHelpCenterProvider(createPostHogHelpCenterProvider(
          posthogClient,
          runtimeConfig.public.posthogFeedbackSurveyId,
        ))
      }
    }

    return posthogClient
  }).catch((error) => {
    $logError('PostHog extension preload failed', error)
    return undefined
  })

  const router = useRouter()
  router.afterEach((to) => {
    nextTick(() => {
      void posthogReady.then((client) => {
        client?.capture('$pageview', {
          current_url: to.fullPath,
        })
      })
    })
  })

  nuxtApp.hook('vue:error', (error) => {
    if (isIgnoredThirdPartyException(error)) return
    void posthogReady.then((client) => {
      client?.captureException(error)
    })
  })

  return {
    provide: {
      posthog: () => posthogClient ?? noopPosthog,
    },
  }
})
